'use client';

import { LoaderCircleIcon } from 'lucide-react';
import { AnimatePresence, AnimationProps, motion } from 'motion/react';
import { useMemo } from 'react';

import ArrowUpRight from '~/assets/icons/arrow--up-right.svg';
import { transition, transitionDelay } from '~/components/molecules/BouncyText';
import { cn } from '~/lib/utils/classname';

import TextSplitter, { LetterPropsGenerator } from '../molecules/TextSplitter';

export const defaultAsType = 'button' as const;
export type DefaultAsType = typeof defaultAsType;

export type OwnProps<E extends React.ElementType> = {
  as?: E;
  children: React.ReactNode;

  variant?: 'light' | 'dimmed' | 'dark';
  level?: 1 | 2;
  loading?: boolean;
};

export type Props<E extends React.ElementType> = OwnProps<E> &
  Omit<React.ComponentProps<E>, keyof OwnProps<E>>;

const letterPropsGenerators: [
  LetterPropsGenerator<AnimationProps>,
  LetterPropsGenerator<AnimationProps>,
] = [
  ({ index }) => ({
    variants: {
      default: { y: 0, opacity: 1 },
      hovering: { y: 6, opacity: 0 },
    },
    transition: { ...transition, delay: index * transitionDelay },
  }),

  ({ index }) => ({
    variants: {
      default: { x: 0 },
      hovering: { x: 20 },
    },
    transition: { ...transition, delay: index * transitionDelay },
  }),
];

const CallToAction = <E extends React.ElementType = DefaultAsType>({
  as: asComponent,
  children,
  variant = 'light',
  level = 1,
  loading,
  className,
  ...rest
}: Props<E>) => {
  const Component = useMemo(
    () => motion.create(asComponent || defaultAsType),
    [asComponent]
  );

  return (
    <Component
      className={cn(
        'group/cta font-heading relative isolate inline-flex cursor-pointer justify-center text-center text-lg font-normal whitespace-nowrap transition-[opacity,background] duration-400 disabled:pointer-events-none! disabled:opacity-50!',
        {
          'text-white': variant === 'dark',
          'text-brand-primary': ['light', 'dimmed'].includes(variant),
          'bg-brand-primary pointer-fine:hocus-visible:bg-white':
            variant === 'dark' && level === 1,
          'pointer-fine:hocus-visible:bg-brand-primary bg-white':
            variant === 'light' && level === 1,
          'pointer-fine:hocus-visible:bg-brand-primary bg-brand-tertiary':
            variant === 'dimmed' && level === 1,
        },
        className
      )}
      initial="default"
      animate="default"
      whileHover="hovering"
      whileFocus="hovering"
      {...rest}
    >
      <motion.div
        className={cn('relative isolate flex justify-center', {
          'h-16 items-center overflow-clip px-8': level === 1,
          'items-baseline pr-5': level === 2,
        })}
        animate={{ opacity: loading ? 0 : 1 }}
      >
        {level === 2 && (
          <motion.div
            className="pointer-events-none absolute left-0 flex h-full flex-col justify-center [&>svg]:mt-1 [&>svg]:w-2"
            variants={{
              default: { x: -16, y: 4, opacity: 0, transition },
              hovering: {
                x: 0,
                y: 0,
                opacity: 1,
                transition: { ...transition, delay: 0.4 },
              },
            }}
          >
            <ArrowUpRight />
          </motion.div>
        )}

        <div className="relative flex">
          {typeof children === 'string' ? (
            <TextSplitter
              text={children}
              useMotion
              generateLetterProps={letterPropsGenerators[level - 1]}
            />
          ) : (
            children
          )}
        </div>

        {level === 1 && (
          <div
            className={cn('absolute', {
              'text-brand-primary': variant === 'dark',
              'text-white': ['light', 'dimmed'].includes(variant),
            })}
            aria-hidden
          >
            {typeof children === 'string' ? (
              <TextSplitter
                text={children}
                useMotion
                generateLetterProps={({ index }) => ({
                  variants: {
                    default: { y: -6, opacity: 0 },
                    hovering: { y: 0, opacity: 1 },
                  },
                  transition: { ...transition, delay: index * transitionDelay },
                })}
              />
            ) : (
              children
            )}
          </div>
        )}

        {level === 2 && (
          <motion.div
            className="pointer-events-none absolute right-0 flex h-full flex-col justify-center [&>svg]:mt-1 [&>svg]:w-2"
            variants={{
              default: {
                x: 0,
                y: 0,
                opacity: 1,
                transition: { ...transition, delay: 0.4 },
              },
              hovering: { x: 16, y: -4, opacity: 0, transition },
            }}
          >
            <ArrowUpRight />
          </motion.div>
        )}
      </motion.div>

      <AnimatePresence>
        {loading && (
          <motion.div
            className={cn(
              'absolute top-0 left-0 grid h-full w-full place-items-center transition-colors',
              {
                'pointer-fine:group-hocus-visible/cta:text-brand-primary text-white':
                  variant === 'dark',
                'text-brand-primary pointer-fine:group-hocus-visible/cta:text-white':
                  ['light', 'dimmed'].includes(variant),
              }
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoaderCircleIcon size="1.5em" className="animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>
    </Component>
  );
};

export default CallToAction;
